<div class="costs-container">

  <!-- Page Header -->
  <div class="page-header">
    <div class="page-title-area">
      <div class="page-icon"><i class="fa-solid fa-coins"></i></div>
      <div>
        <h1 class="page-title">System Costs & Usage</h1>
        <p class="page-subtitle">System-wide cost and token analytics</p>
      </div>
    </div>
    <div class="page-actions">
      <div class="preset-nav">
        @for (preset of TimePresets; track preset.key) {
          <button class="btn-preset"
                  [class.active]="SelectedPreset === preset.key"
                  (click)="SelectPreset(preset.key)">
            {{ preset.label }}
          </button>
        }
      </div>
      @if (SelectedPreset === 'custom') {
        <div class="custom-range">
          <input type="date" [(ngModel)]="CustomStartDate" />
          <span class="custom-range-sep">to</span>
          <input type="date" [(ngModel)]="CustomEndDate" />
          <button class="btn-apply" (click)="ApplyCustomRange()">Apply</button>
        </div>
      }
      <button class="btn-action" (click)="LoadData()">
        <i class="fa-solid fa-arrows-rotate"></i> Refresh
      </button>
    </div>
  </div>

  <!-- Error Banner -->
  @if (Error) {
    <div class="error-banner">
      <i class="fa-solid fa-circle-exclamation"></i>
      {{ Error }}
      <button (click)="Error = null">&times;</button>
    </div>
  }

  <!-- Loading State -->
  @if (Loading) {
    <div class="loading-overlay"><div class="spinner"></div> Loading cost data...</div>
  }

  @if (!Loading && Summary) {

    <!-- KPI Cards Row -->
    <div class="stats-cards">
      <div class="stat-card">
        <span class="stat-value cost-value">{{ FormatCost(Summary.totalCost) }}</span>
        <span class="stat-label">Total Cost</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{{ FormatTokens(Summary.totalTokens) }}</span>
        <span class="stat-label">Total Tokens</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{{ FormatCostPrecise(Summary.avgCostPerRun) }}</span>
        <span class="stat-label">Avg Cost / Run</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{{ FormatNumber(Summary.totalPromptRuns) }}</span>
        <span class="stat-label">Total Prompt Runs</span>
      </div>
      <div class="stat-card">
        <span class="stat-value accent-value">{{ FormatPercent(Summary.cacheHitRate) }}</span>
        <span class="stat-label">Cache Hit Rate</span>
      </div>
      <div class="stat-card token-split-card">
        <div class="token-split-values">
          <span class="token-split-item">
            <span class="token-split-dot prompt-dot"></span>
            Prompt {{ FormatTokens(Summary.totalPromptTokens) }}
          </span>
          <span class="token-split-item">
            <span class="token-split-dot completion-dot"></span>
            Completion {{ FormatTokens(Summary.totalCompletionTokens) }}
          </span>
        </div>
        <span class="stat-label">Token Split</span>
      </div>
    </div>

    <!-- Cost by Model (horizontal bar chart) -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title"><i class="fa-solid fa-microchip"></i> Cost by Model</h3>
      </div>
      @if (ModelCosts.length > 0) {
        <div class="hbar-chart">
          @for (row of ModelCosts; track row.modelName + row.vendorName) {
            <div class="hbar-row">
              <div class="hbar-label">
                <span class="hbar-name">{{ row.modelName }}</span>
                <span class="hbar-sub">{{ row.vendorName }}</span>
              </div>
              <div class="hbar-track">
                <div class="hbar-fill hbar-fill-model"
                     [style.width.%]="GetHBarWidth(row.totalCost, MaxModelCost)"></div>
              </div>
              <div class="hbar-stats">
                <span class="hbar-cost">{{ FormatCostPrecise(row.totalCost) }}</span>
                <span class="hbar-pct">{{ GetModelCostPercent(row.totalCost).toFixed(1) }}%</span>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="empty-state-sm">
          <i class="fa-solid fa-microchip"></i>
          <p>No model cost data available</p>
        </div>
      }
    </div>

    <!-- Cost by Organization (horizontal bar chart) -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title"><i class="fa-solid fa-building"></i> Cost by Organization</h3>
      </div>
      @if (OrgCosts.length > 0) {
        <div class="hbar-chart">
          @for (row of OrgCosts; track row.organizationName) {
            <div class="hbar-row">
              <div class="hbar-label">
                <span class="hbar-name">{{ row.organizationName }}</span>
              </div>
              <div class="hbar-track">
                <div class="hbar-fill hbar-fill-org"
                     [style.width.%]="GetHBarWidth(row.totalCost, MaxOrgCost)"></div>
              </div>
              <div class="hbar-stats">
                <span class="hbar-cost">{{ FormatCostPrecise(row.totalCost) }}</span>
                <span class="hbar-pct">{{ GetOrgCostPercent(row.totalCost).toFixed(1) }}%</span>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="empty-state-sm">
          <i class="fa-solid fa-building"></i>
          <p>No organization cost data available</p>
        </div>
      }
    </div>

    <!-- Daily Cost Trend (vertical bar chart) -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title"><i class="fa-solid fa-chart-column"></i> Daily Cost Trend</h3>
      </div>
      @if (FilledDailyCosts.length > 0) {
        <div class="chart-container">
          <div class="chart-with-axis">
            <div class="y-axis">
              @for (tick of CostYAxisTicks; track $index) {
                <span class="y-tick">{{ FormatAxisCost(tick) }}</span>
              }
            </div>
            <div class="chart-area">
              @for (tick of CostYAxisTicks; track $index) {
                @if ($index > 0) {
                  <div class="gridline" [style.bottom.%]="GetGridlinePosition(tick, NiceMaxCost)"></div>
                }
              }
              <div class="bar-chart">
                @for (row of FilledDailyCosts; track row.day) {
                  <div class="bar-col"
                       [class.bar-col-zero]="row.totalCost === 0"
                       [title]="FormatDay(row.day) + ': ' + FormatCostPrecise(row.totalCost)">
                    @if (row.totalCost > 0) {
                      <span class="bar-value">{{ FormatCostPrecise(row.totalCost) }}</span>
                    }
                    <div class="bar" [style.height.%]="GetCostBarHeight(row.totalCost)"></div>
                    <span class="bar-label">{{ GetShortDay(row.day) }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      } @else {
        <div class="empty-state-sm">
          <i class="fa-solid fa-chart-column"></i>
          <p>No daily cost data available</p>
        </div>
      }
    </div>

    <!-- Token Distribution (stacked horizontal bar) -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title"><i class="fa-solid fa-chart-pie"></i> Token Distribution</h3>
      </div>
      @if (TotalTokensForDistribution > 0) {
        <div class="token-dist">
          <div class="token-dist-bar">
            <div class="token-dist-segment prompt-segment"
                 [style.width.%]="GetTokenSegmentWidth(Summary.totalPromptTokens)"
                 [title]="'Prompt: ' + FormatTokens(Summary.totalPromptTokens) + ' (' + GetTokenSegmentWidth(Summary.totalPromptTokens).toFixed(1) + '%)'">
            </div>
            <div class="token-dist-segment completion-segment"
                 [style.width.%]="GetTokenSegmentWidth(Summary.totalCompletionTokens)"
                 [title]="'Completion: ' + FormatTokens(Summary.totalCompletionTokens) + ' (' + GetTokenSegmentWidth(Summary.totalCompletionTokens).toFixed(1) + '%)'">
            </div>
            <div class="token-dist-segment cache-segment"
                 [style.width.%]="GetTokenSegmentWidth(Summary.totalCacheReadTokens)"
                 [title]="'Cache Read: ' + FormatTokens(Summary.totalCacheReadTokens) + ' (' + GetTokenSegmentWidth(Summary.totalCacheReadTokens).toFixed(1) + '%)'">
            </div>
          </div>
          <div class="token-dist-legend">
            <div class="token-legend-item">
              <span class="token-legend-dot prompt-dot"></span>
              <span class="token-legend-label">Prompt Tokens</span>
              <span class="token-legend-value">{{ FormatTokens(Summary.totalPromptTokens) }}</span>
              <span class="token-legend-pct">{{ GetTokenSegmentWidth(Summary.totalPromptTokens).toFixed(1) }}%</span>
            </div>
            <div class="token-legend-item">
              <span class="token-legend-dot completion-dot"></span>
              <span class="token-legend-label">Completion Tokens</span>
              <span class="token-legend-value">{{ FormatTokens(Summary.totalCompletionTokens) }}</span>
              <span class="token-legend-pct">{{ GetTokenSegmentWidth(Summary.totalCompletionTokens).toFixed(1) }}%</span>
            </div>
            <div class="token-legend-item">
              <span class="token-legend-dot cache-dot"></span>
              <span class="token-legend-label">Cache Read Tokens</span>
              <span class="token-legend-value">{{ FormatTokens(Summary.totalCacheReadTokens) }}</span>
              <span class="token-legend-pct">{{ GetTokenSegmentWidth(Summary.totalCacheReadTokens).toFixed(1) }}%</span>
            </div>
          </div>
        </div>
      } @else {
        <div class="empty-state-sm">
          <i class="fa-solid fa-chart-pie"></i>
          <p>No token distribution data available</p>
        </div>
      }
    </div>
  }

  @if (!Loading && !Summary && !Error) {
    <div class="empty-state">
      <i class="fa-solid fa-coins"></i>
      <p>No cost data available for the selected time period</p>
    </div>
  }

</div>
